import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {useAppSelector} from '@/hooks';
import {Text, TextSize, TextType} from '@Components/text';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import styles from './poster-editor-version-history-panel.module.scss';
import {setVersionHistoryOverlayVisibility, setVersionHistorySelectedVersion} from '@Components/poster-editor/poster-editor-reducer';
import {loadPosterFromHashedId} from '@PosterWhiteboard/poster/poster-init';
import {copyPoster, fetchPosterVersions, closeVersionHistoryPanel} from '@Components/poster-editor/library/poster-editor-library';
import type {PosterVersionData} from '@Components/poster-editor/library/poster-editor-library';
import {VersionHistoryList} from './components/version-history-list';
import {isUserPAYG} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';
import {InformationBox} from '@Components/contextual-menu/components/information-box';
import {showMessageGrowl} from '@Components/message-growl';
import {GROWL_TYPE} from '@Components/message-growl/message-growl.types';
import {openPremiumPricingModal} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export interface PosterEditorVersionHistoryPanelProps {
  isFromMobilePanel?: boolean;
  onMobilePanelClose?: () => void;
}

export function PosterEditorVersionHistoryPanel({isFromMobilePanel = false, onMobilePanelClose = noop}: PosterEditorVersionHistoryPanelProps): ReactElement {
  const [versions, setVersions] = useState<PosterVersionData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadingVersionIndex, setLoadingVersionIndex] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const posterHashId = useAppSelector((state) => {
    return state.posterEditor.posterObject?.hashedID;
  });
  const posterId = useAppSelector((state) => {
    return state.posterEditor.posterObject?.id;
  });

  const fetchVersions = async (): Promise<void> => {
    if (!posterId) return;

    setIsInitialLoading(true);
    const response = await fetchPosterVersions(posterId, isUserPAYG());
    setVersions(response);
    setIsInitialLoading(false);
  };

  const handleVersionSelect = async (index: number): Promise<void> => {
    if (loadingVersionIndex !== null || index === selectedIndex) {
      return;
    }

    try {
      setSelectedIndex(index);
      setLoadingVersionIndex(index);
      const selectedVersion = versions[index];
      window.PMW.redux.store.dispatch(setVersionHistorySelectedVersion(selectedVersion));
      window.PMW.redux.store.dispatch(setVersionHistoryOverlayVisibility(true));

      if (!posterHashId) {
        console.error('No poster hash ID available');
        return;
      }

      await loadPosterFromHashedId({posterHashId, posterVersionId: selectedVersion.versionId, isForGalleryTemplate: false}, false, false);
    } catch (err) {
      console.error('Failed to load version:', err);
    } finally {
      window.PMW.redux.store.dispatch(setVersionHistoryOverlayVisibility(false));
      window.PMW.redux.store.dispatch(setVersionHistorySelectedVersion(null));
      setLoadingVersionIndex(null);
    }
  };

  const handleDuplicate = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.VERSION_HISTORY_DUPLICATE);

    try {
      const selectedVersion = versions[selectedIndex];
      if (selectedVersion?.versionId) {
        copyPoster(selectedVersion.versionId);
      }
    } catch (err) {
      console.error('Failed to duplicate version:', err);
    }
  };

  const handleRestore = async (): Promise<void> => {
    trackPosterBuilderGA4Events(GA4EventName.VERSION_HISTORY_RESTORE);

    try {
      setIsRestoring(true);
      const selectedVersion = versions[selectedIndex];
      if (!selectedVersion?.versionId || !posterHashId) {
        console.error('No version selected or poster hash ID available');
        return;
      }

      closeVersionHistoryPanel(false);
      await loadPosterFromHashedId({posterHashId, posterVersionId: selectedVersion.versionId, isForGalleryTemplate: false}, true, false);

      showMessageGrowl({
        text: window.i18next.t('pmwjs_version_restore_snackbar_text'),
        type: GROWL_TYPE.NEUTRAL,
        interval: 3000,
        showCloseIcon: false,
      });

      if (isFromMobilePanel && onMobilePanelClose) {
        onMobilePanelClose();
      }
    } catch (err) {
      console.error('Failed to restore version:', err);
    } finally {
      setIsRestoring(false);
    }
  };

  const getHeader = (): ReactElement => {
    return (
      <ContextualMenuHeader id="version-history-panel-header">
        <ContextualMenuTextHeader text={window.i18next.t('pmwjs_version_history')} />
      </ContextualMenuHeader>
    );
  };

  const getFooter = (): ReactElement => {
    return (
      <div className="flex-h-row flex-items-center gap-2 spacing-p-4 _full-width _full-height">
        <Button
          type={Type.SECONDARY}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          icon="icon-duplicate"
          text={window.i18next.t('pmwjs_duplicate')}
          onClick={handleDuplicate}
          customClasses="flex-1"
        />
        <Button
          type={Type.PRIMARY}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          icon="icon-rotate-acw"
          text={window.i18next.t('pmwjs_restore')}
          onClick={handleRestore}
          disabled={loadingVersionIndex === selectedIndex || selectedIndex === 0}
          isLoading={isRestoring}
          customClasses="flex-1"
        />
      </div>
    );
  };

  const getPremiumNotice = (): ReactElement | null => {
    if (!isUserPAYG()) {
      return null;
    }

    return (
      <InformationBox className={`flexbox flex-items-center gap-0 ${styles.customInformationBox}`}>
        <div>
          <Text val={window.i18next.t('pmwjs_version_history_limited')} type={TextType.BODY} size={TextSize.XSMALL} bold />
          <br />
          <Text val={window.i18next.t('pmwjs_unlock_premium_version_history')} type={TextType.BODY} size={TextSize.XSMALL} />
          <Text
            val={window.i18next.t('pmwjs_upgrade')}
            className={styles.upgradeLink}
            type={TextType.BODY}
            size={TextSize.XSMALL}
            underline
            onClick={(): void => {
              openPremiumPricingModal(false);
            }}
          />
        </div>
      </InformationBox>
    );
  };

  useEffect(() => {
    if (posterId) {
      void fetchVersions();
    }
  }, []);

  return (
    <ContextualMenu
      id="poster-editor-version-history-panel"
      contentKey="version-history-panel"
      header={isFromMobilePanel ? undefined : getHeader()}
      footerContent={getFooter()}
      contentClassName="spacing-p-4"
      includeLeftRightPadding={false}
    >
      {getPremiumNotice()}
      <VersionHistoryList
        versions={versions}
        selectedIndex={selectedIndex}
        loadingVersionIndex={loadingVersionIndex}
        isInitialLoading={isInitialLoading}
        onVersionSelect={handleVersionSelect}
      />
    </ContextualMenu>
  );
}
